import React, {
  ReactNode, useCallback, useLayoutEffect, useMemo, useRef,
} from 'react';
import { Button } from 'react-bootstrap';

import FilterChip from 'components/Flux/Personalized/MyStudies/FilterSummary/FilterChip';
import { buildFilterChips } from 'helper/flux/myStudiesFilters';
import formatStudyCount from 'helper/flux/myStudiesSummary';
import {
  MyStudiesFilterChip,
  MyStudiesFilters,
  MyStudiesFiltersChange,
  SetFiltersOptions,
} from 'types/Flux/Personalized/myStudiesFilters';

import './index.scss';

/** Remover um chip vale na hora, inclusive o da busca (que esperaria o debounce de 450 ms). */
const IMMEDIATE: SetFiltersOptions = { immediate: true };

export interface FilterSummaryProps {
  /** Filtros aplicados: `filters` do `useMyStudiesFilters`. */
  filters: MyStudiesFilters;
  /**
   * Texto do campo de busca: `searchDraft` do hook. O chip da busca mostra o que foi digitado,
   * como o protótipo (.dc.html:679), e some assim que o campo é apagado.
   */
  searchDraft: string;
  /** Nome da empresa escolhida (#2000); sem ele, o chip mostra o id. */
  companyLabel?: string;
  /** Nome do usuário escolhido em Criado por (#2000); sem ele, o `nome` guardado ou o id. */
  userLabel?: string;
  /**
   * Total de estudos da consulta: `data?.total`, que mantém a contagem anterior durante o
   * `pending` (como o protótipo, .dc.html:410). No `error-initial` da #1992 a #1999 passa
   * `undefined`: o `data` que sobrou é de outro filtro. Só vale inteiro >= 0; fora disso, "—".
   */
  total?: number;
  /**
   * Primeira carga da lista: o estado `initial` da #1992 (skeleton). Mostra "—" e diz
   * "Carregando o total de estudos" ao leitor de tela. Sem carga e sem total válido (ex.:
   * `error-initial`), o "—" diz "Total de estudos indisponível".
   */
  isInitialLoading: boolean;
  /**
   * Consulta nova a caminho: mostra "Atualizando lista…". A #1999 passa
   * `state === 'pending' || searchDraft.trim() !== filters.busca` (a busca digitada esperando o
   * debounce também conta).
   */
  isPending: boolean;
  /**
   * `setFilters` do hook: recebe a mudança de cada chip removido, sempre com
   * `{ immediate: true }`.
   */
  onFiltersChange: (change: MyStudiesFiltersChange, options: SetFiltersOptions) => void;
  /** `clearFilters` do hook ("Limpar filtros", D6). */
  onClearFilters: () => void;
  /** Slot à direita para o indicador "atualizado há N s" + "Atualizar" (#2004). */
  updateIndicator?: ReactNode;
}

/**
 * Resumo da lista da aba Meus Estudos (#1997, .dc.html:222-238): contagem, chips removíveis,
 * "Limpar filtros" e, à direita, o slot do indicador de atualização. Visualmente é o rodapé do
 * card de filtros (3º filho do card de .dc.html:131): divisor no topo e cantos de baixo.
 *
 * Controlado por props, sem chamar o `useMyStudiesFilters`: a #1999 o monta no `footer` da barra
 * da #1995, com a instância ÚNICA do hook da tela e o resultado da consulta:
 * `<FilterBar … footer={<FilterSummary filters={filters} searchDraft={searchDraft}
 * onFiltersChange={setFilters} onClearFilters={clearFilters} … />} />`.
 *
 * - Os chips saem de `buildFilterChips({ ...filters, busca: searchDraft })`. "Limpar filtros"
 *   aparece só com chip, e isso é sempre o `hasActiveFilters` do hook.
 * - Cada chip (`FilterChip`) é a `Tag` do DS com `removeLabel` (#1985): o botão "Remover filtro
 *   <rótulo>" pede a mudança do chip com `{ immediate: true }`.
 * - A contagem e "Atualizando lista…" ficam numa região `status` sempre montada, para o leitor de
 *   tela ler a troca (o `aria-busy` da lista quase não é lido).
 * - Foco: o botão removido some e o foco iria para o `<body>`. Ele vai para o chip que ocupou o
 *   lugar (ou o anterior) e, sem chips, para o próprio resumo ("Resumo dos filtros"); só uma vez
 *   por remoção, e nunca sai de outro elemento.
 */
export default function FilterSummary({
  filters,
  searchDraft,
  companyLabel,
  userLabel,
  total,
  isInitialLoading,
  isPending,
  onFiltersChange,
  onClearFilters,
  updateIndicator,
}: FilterSummaryProps) {
  const chips = useMemo(
    () => buildFilterChips({ ...filters, busca: searchDraft }, { companyLabel, userLabel }),
    [filters, searchDraft, companyLabel, userLabel],
  );
  const count = isInitialLoading ? null : formatStudyCount(total);

  const rootRef = useRef<HTMLDivElement>(null);
  const chipListRef = useRef<HTMLUListElement>(null);
  /** Posição do chip removido (0 em "Limpar filtros"), à espera da lista nova de chips. */
  const removedAt = useRef<number | null>(null);
  const chipKeys = chips.map((chip) => chip.key).join('|');

  useLayoutEffect(() => {
    const index = removedAt.current;
    removedAt.current = null;
    // Só devolve o foco que caiu no <body> com o botão removido
    const { activeElement } = document;
    if (index === null || (activeElement !== null && activeElement !== document.body)) return;
    const buttons = chipListRef.current?.querySelectorAll<HTMLButtonElement>('button') ?? [];
    const target = buttons.length > 0
      ? buttons[Math.min(index, buttons.length - 1)]
      : rootRef.current;
    target?.focus();
  }, [chipKeys]);

  const removeChip = useCallback((chip: MyStudiesFilterChip, index: number) => {
    removedAt.current = index;
    onFiltersChange(chip.change, IMMEDIATE);
  }, [onFiltersChange]);

  const clearAll = useCallback(() => {
    removedAt.current = 0;
    onClearFilters();
  }, [onClearFilters]);

  return (
    <div
      ref={rootRef}
      role="group"
      aria-label="Resumo dos filtros"
      className="my-studies-filter-summary"
      tabIndex={-1}
      data-testid="my-studies-filter-summary"
    >
      <div role="status" className="my-studies-filter-summary-status">
        <span className="my-studies-filter-summary-count">
          {count ?? (
            <>
              <span aria-hidden="true">—</span>
              <span className="visually-hidden">
                {isInitialLoading ? 'Carregando o total de estudos' : 'Total de estudos indisponível'}
              </span>
            </>
          )}
        </span>
        {isPending && <span className="my-studies-filter-summary-pending">Atualizando lista…</span>}
      </div>

      {chips.length > 0 && (
        <>
          <ul ref={chipListRef} className="my-studies-filter-summary-chips" aria-label="Filtros aplicados">
            {chips.map((chip, index) => (
              <FilterChip key={chip.key} chip={chip} index={index} onRemove={removeChip} />
            ))}
          </ul>
          <Button variant="link" className="my-studies-filter-summary-clear" onClick={clearAll}>
            Limpar filtros
          </Button>
        </>
      )}

      {updateIndicator && <div className="my-studies-filter-summary-trailing">{updateIndicator}</div>}
    </div>
  );
}
